import React, { useCallback, useEffect, useState } from 'react'
import {
	Avatar,
	Badge,
	Button,
	Card,
	Col,
	Descriptions,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Spin,
	Steps,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd'
import {
	ArrowLeftOutlined,
	CheckCircleOutlined,
	CloseCircleOutlined,
	EditOutlined,
	UserOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { Colors } from '../../common/constant'
import { useThemeColors } from '../../common/ThemeContext'
import { leaveService } from '../../common/service'

type OverallStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled'
type ApprovalStatus = 'Pending Approval' | 'Approved' | 'Rejected'

interface ApprovalStep {
	level: number
	role: string
	approver?: { id?: string; name?: string; photoUrl?: string } | null
	status: ApprovalStatus
	comment?: string | null
	actionAt?: string | null
}

interface LeaveDetail {
	id: string
	leaveType?: string
	startDate?: string
	endDate?: string
	halfDayPeriod?: 'Morning' | 'Afternoon' | null
	days?: number
	reason?: string
	overallStatus?: OverallStatus
	attachmentUrls?: string[]
	requester?: { id?: string; name?: string; photoUrl?: string } | null
	approvalWorkflow?: ApprovalStep[]
	createdAt?: string
	updatedAt?: string
}

interface LeaveBalance {
	staffId: string
	year: number
	entitlements: Record<string, number>
	used: Record<string, number>
	remaining: Record<string, number>
}

// Cambodia Labour Law defaults — used only when the API call to /settings fails
const LEAVE_TYPES_FALLBACK = ['Annual', 'Sick', 'Maternity', 'Paternity', 'Special', 'Unpaid']

const overallStatusColor: Record<OverallStatus, string> = {
	Pending: 'gold',
	Approved: 'green',
	Rejected: 'red',
	Cancelled: 'default',
}

const approvalStatusColor: Record<ApprovalStatus, string> = {
	'Pending Approval': 'gold',
	Approved: 'green',
	Rejected: 'red',
}

const stepStatus = (s: ApprovalStatus): 'finish' | 'process' | 'error' | 'wait' => {
	if (s === 'Approved') return 'finish'
	if (s === 'Rejected') return 'error'
	if (s === 'Pending Approval') return 'process'
	return 'wait'
}

type LeaveDetailProps = {
	leaveId?: string
	embedded?: boolean
}

const LeaveDetail: React.FC<LeaveDetailProps> = ({ leaveId, embedded = false }) => {
	const { primary } = useThemeColors()
	const { id } = useParams<{ id: string }>()
	const targetId = leaveId || id
	const navigate = useNavigate()

	const [loading, setLoading] = useState(true)
	const [detail, setDetail] = useState<LeaveDetail | null>(null)

	const [actionOpen, setActionOpen] = useState(false)
	const [actionType, setActionType] = useState<'approve' | 'reject'>('approve')
	const [actionComment, setActionComment] = useState('')
	const [actionLoading, setActionLoading] = useState(false)

	// ── Leave balance ─────────────────────────────────────────────────────────
	const [balance, setBalance] = useState<LeaveBalance | null>(null)
	const [balanceLoading, setBalanceLoading] = useState(false)
	const [balanceEditOpen, setBalanceEditOpen] = useState(false)
	const [balanceSaving, setBalanceSaving] = useState(false)
	const [balanceForm] = Form.useForm<Record<string, number>>()
	const [leaveTypes, setLeaveTypes] = useState<string[]>(LEAVE_TYPES_FALLBACK)

	const fetchBalance = useCallback(async (staffId: string) => {
		try {
			setBalanceLoading(true)
			const resp: any = await leaveService.getBalance(staffId)
			const data: LeaveBalance = resp?.data?.data ?? resp?.data ?? resp
			setBalance(data)
		} catch {
			// Non-fatal — balance is supplementary info
		} finally {
			setBalanceLoading(false)
		}
	}, [])

	// Fetch configured leave types from settings (non-fatal fallback to constant)
	useEffect(() => {
		leaveService
			.getLeaveSettings()
			.then((res: any) => {
				const types: Array<{ key: string; enabled: boolean }> = res?.data?.types ?? []
				const enabled = types.filter((t) => t.enabled).map((t) => t.key)
				if (enabled.length) setLeaveTypes(enabled)
			})
			.catch(() => {
				/* keep fallback */
			})
	}, [])

	const fetchDetail = useCallback(async () => {
		if (!targetId) return
		try {
			setLoading(true)
			const response: any = await leaveService.detail(targetId)
			const data: LeaveDetail = response?.data?.data ?? response?.data ?? response
			setDetail(data)
		} catch (err: any) {
			message.error(err?.response?.data?.message || 'Failed to load leave details')
		} finally {
			setLoading(false)
		}
	}, [targetId])

	useEffect(() => {
		fetchDetail()
	}, [fetchDetail])

	// Fetch balance when detail (and requester ID) is available
	useEffect(() => {
		if (detail?.requester?.id) {
			fetchBalance(detail.requester.id)
		}
	}, [detail?.requester?.id, fetchBalance])

	const onSaveEntitlements = async () => {
		if (!detail?.requester?.id || !balance) return
		try {
			setBalanceSaving(true)
			const values = balanceForm.getFieldsValue()
			await leaveService.setBalance(detail.requester.id, {
				year: balance.year,
				...values,
			})
			message.success('Entitlements updated')
			setBalanceEditOpen(false)
			fetchBalance(detail.requester.id)
		} catch (err: any) {
			message.error(err?.response?.data?.message || 'Failed to update entitlements')
		} finally {
			setBalanceSaving(false)
		}
	}

	const onConfirmAction = async () => {
		if (!detail?.id) return
		if (actionType === 'reject' && !actionComment.trim()) {
			message.warning('A reason is required when rejecting.')
			return
		}
		try {
			setActionLoading(true)
			if (actionType === 'approve') {
				await leaveService.approve(detail.id, actionComment || undefined)
				message.success('Leave request approved')
			} else {
				await leaveService.reject(detail.id, actionComment)
				message.success('Leave request rejected')
			}
			setActionOpen(false)
			setActionComment('')
			fetchDetail()
		} catch (err: any) {
			message.error(err?.response?.data?.message || 'Action failed')
		} finally {
			setActionLoading(false)
		}
	}

	const isPending = detail?.overallStatus === 'Pending'

	// Approval workflow ordered by level ascending from the API
	const workflow = detail?.approvalWorkflow ?? []

	return (
		<>
			{!embedded && <GlobalHeader />}

			{/* Page title */}
			{!embedded && (
				<Card style={{ margin: '10px', textAlign: 'center', borderRadius: '8px' }}>
					<Typography.Title
						aria-level={2}
						style={{
							color: primary,
							fontFamily: 'Times New Roman',
							marginBottom: 0,
						}}
					>
						Leave Request Details
					</Typography.Title>
				</Card>
			)}

			<Card style={{ margin: embedded ? '0' : '0 16px 16px' }}>
				{/* Back button */}
				{!embedded && (
					<Button
						icon={<ArrowLeftOutlined />}
						type='link'
						style={{ paddingLeft: 0, marginBottom: 16, color: primary }}
						onClick={() => navigate('/leaves')}
					>
						Back to List
					</Button>
				)}

				{loading ? (
					<div style={{ textAlign: 'center', padding: 48 }}>
						<Spin size='large' />
					</div>
				) : !detail ? (
					<Typography.Text type='danger'>Leave request not found.</Typography.Text>
				) : (
					<>
						{/* ─── Overall Status Badge ─────────────────────────────────────── */}
						<Row justify='end' style={{ marginBottom: 16 }}>
							<Tag
								color={overallStatusColor[detail.overallStatus ?? 'Pending']}
								style={{ fontSize: 14, padding: '4px 12px' }}
							>
								{detail.overallStatus ?? '-'}
							</Tag>
						</Row>

						{/* ─── Leave Info ───────────────────────────────────────────────── */}
						<Card
							type='inner'
							title='Leave Information'
							style={{ marginBottom: 16, borderRadius: 8 }}
						>
							<Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size='small'>
								<Descriptions.Item label='Leave ID'>{detail.id}</Descriptions.Item>
								<Descriptions.Item label='Leave Type'>
									{detail.leaveType ?? '-'}
								</Descriptions.Item>
								<Descriptions.Item label='Duration'>
									{detail.days != null
										? `${detail.days} day${detail.days > 1 ? 's' : ''}`
										: '-'}
								</Descriptions.Item>
								<Descriptions.Item label='Start Date'>
									{detail.startDate
										? dayjs(detail.startDate).format('YYYY-MM-DD')
										: '-'}
								</Descriptions.Item>
								<Descriptions.Item label='End Date'>
									{detail.endDate
										? dayjs(detail.endDate).format('YYYY-MM-DD')
										: '-'}
								</Descriptions.Item>
								{detail.halfDayPeriod != null && (
									<Descriptions.Item label='Half Day'>
										{detail.halfDayPeriod}
									</Descriptions.Item>
								)}
								<Descriptions.Item label='Submitted At'>
									{detail.createdAt
										? dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm')
										: '-'}
								</Descriptions.Item>
								<Descriptions.Item label='Reason' span={3}>
									{detail.reason ?? '-'}
								</Descriptions.Item>
							</Descriptions>
						</Card>

						{/* ─── Requester ────────────────────────────────────────────────── */}
						{detail.requester && (
							<Card
								type='inner'
								title='Requester'
								style={{ marginBottom: 16, borderRadius: 8 }}
							>
								<Row align='middle' gutter={12}>
									<Col>
										{detail.requester.photoUrl ? (
											<Avatar src={detail.requester.photoUrl} size={48} />
										) : (
											<Avatar icon={<UserOutlined />} size={48} />
										)}
									</Col>
									<Col>
										<Typography.Text strong>
											{detail.requester.name ?? '-'}
										</Typography.Text>
										<br />
										<Typography.Text type='secondary' style={{ fontSize: 12 }}>
											{detail.requester.id}
										</Typography.Text>
									</Col>
								</Row>
							</Card>
						)}

						{/* ─── Leave Balance ────────────────────────────────────────────── */}
						{detail.requester?.id && (
							<Card
								type='inner'
								title={
									<Row justify='space-between' align='middle'>
										<Col>
											<Typography.Text strong>
												Leave Balance (
												{balance?.year ?? new Date().getFullYear()})
											</Typography.Text>
										</Col>
										<Col>
											<Tooltip title='Edit Entitlements'>
												<Button
													size='small'
													icon={<EditOutlined />}
													onClick={() => {
														if (balance) {
															balanceForm.setFieldsValue(
																balance.entitlements,
															)
														}
														setBalanceEditOpen(true)
													}}
												>
													Edit
												</Button>
											</Tooltip>
										</Col>
									</Row>
								}
								style={{ marginBottom: 16, borderRadius: 8 }}
							>
								{balanceLoading ? (
									<Spin size='small' />
								) : balance ? (
									<Row gutter={[16, 8]}>
										{leaveTypes.map((type) => {
											const remaining = balance.remaining[type] ?? 0
											const entitlement = balance.entitlements[type] ?? 0
											const used = balance.used[type] ?? 0
											const pct =
												entitlement > 0
													? Math.round((remaining / entitlement) * 100)
													: 0
											const color =
												pct >= 50
													? '#52c41a'
													: pct >= 20
														? '#faad14'
														: '#ff4d4f'
											return (
												<Col key={type} xs={12} sm={8} md={6} lg={4}>
													<Card
														size='small'
														bordered
														style={{
															textAlign: 'center',
															borderRadius: 8,
														}}
													>
														<Typography.Text
															type='secondary'
															style={{ fontSize: 11 }}
														>
															{type}
														</Typography.Text>
														<div>
															<Typography.Text
																strong
																style={{ fontSize: 20, color }}
															>
																{remaining}
															</Typography.Text>
															<Typography.Text
																type='secondary'
																style={{ fontSize: 11 }}
															>
																{' '}
																/{entitlement} days
															</Typography.Text>
														</div>
														<Typography.Text
															type='secondary'
															style={{ fontSize: 10 }}
														>
															Used: {used}
														</Typography.Text>
													</Card>
												</Col>
											)
										})}
									</Row>
								) : (
									<Typography.Text type='secondary'>
										Balance unavailable
									</Typography.Text>
								)}
							</Card>
						)}

						{/* ─── Attachments ──────────────────────────────────────────────── */}
						{detail.attachmentUrls && detail.attachmentUrls.length > 0 && (
							<Card
								type='inner'
								title='Attachments'
								style={{ marginBottom: 16, borderRadius: 8 }}
							>
								<Row gutter={[12, 12]}>
									{detail.attachmentUrls.map((url, idx) => (
										<Col key={idx}>
											<a href={url} target='_blank' rel='noreferrer'>
												<img
													src={url}
													alt={`Attachment ${idx + 1}`}
													style={{
														width: 100,
														height: 100,
														objectFit: 'cover',
														borderRadius: 8,
														border: '1px solid #f0f0f0',
													}}
												/>
											</a>
										</Col>
									))}
								</Row>
							</Card>
						)}

						{/* ─── Approval Workflow ────────────────────────────────────────── */}
						<Card
							type='inner'
							title='Approval Workflow'
							style={{ marginBottom: 16, borderRadius: 8 }}
						>
							{workflow.length > 0 && (
								<Typography.Text
									type='secondary'
									style={{ display: 'block', marginBottom: 12 }}
								>
									{workflow.map((s) => s.role).join(' → ')}
								</Typography.Text>
							)}
							<Steps
								direction='vertical'
								items={workflow.map((step) => ({
									title: (
										<Row align='middle' gutter={8}>
											<Col>
												<Typography.Text strong>
													{step.role}
												</Typography.Text>
											</Col>
											<Col>
												<Tag color={approvalStatusColor[step.status]}>
													{step.status}
												</Tag>
											</Col>
										</Row>
									),
									description: (
										<div style={{ marginTop: 4 }}>
											{step.approver ? (
												<Row
													align='middle'
													gutter={8}
													style={{ marginBottom: 4 }}
												>
													<Col>
														{step.approver.photoUrl ? (
															<Avatar
																src={step.approver.photoUrl}
																size={24}
															/>
														) : (
															<Avatar
																icon={<UserOutlined />}
																size={24}
															/>
														)}
													</Col>
													<Col>
														<Typography.Text style={{ fontSize: 13 }}>
															{step.approver.name ?? '-'}
														</Typography.Text>
													</Col>
												</Row>
											) : (
												<Typography.Text
													type='secondary'
													style={{ fontSize: 13 }}
												>
													{step.role === 'Admin'
														? 'Any Admin'
														: 'Not assigned'}
												</Typography.Text>
											)}
											{step.comment && (
												<Typography.Text
													type='secondary'
													style={{ fontSize: 12, display: 'block' }}
												>
													Comment: {step.comment}
												</Typography.Text>
											)}
											{step.actionAt && (
												<Typography.Text
													type='secondary'
													style={{ fontSize: 12, display: 'block' }}
												>
													{dayjs(step.actionAt).format(
														'YYYY-MM-DD HH:mm',
													)}
												</Typography.Text>
											)}
										</div>
									),
									status: stepStatus(step.status),
									icon:
										step.status === 'Approved' ? (
											<CheckCircleOutlined style={{ color: '#52c41a' }} />
										) : step.status === 'Rejected' ? (
											<CloseCircleOutlined style={{ color: '#ff4d4f' }} />
										) : undefined,
								}))}
							/>
						</Card>

						{/* ─── Admin Actions ────────────────────────────────────────────── */}
						{isPending && (
							<Row gutter={12}>
								<Col>
									<Button
										type='primary'
										icon={<CheckCircleOutlined />}
										onClick={() => {
											setActionType('approve')
											setActionComment('')
											setActionOpen(true)
										}}
									>
										Approve
									</Button>
								</Col>
								<Col>
									<Button
										danger
										icon={<CloseCircleOutlined />}
										onClick={() => {
											setActionType('reject')
											setActionComment('')
											setActionOpen(true)
										}}
									>
										Reject
									</Button>
								</Col>
							</Row>
						)}
					</>
				)}
			</Card>

			{/* ─── Action Modal ─────────────────────────────────────────────────── */}
			<Modal
				open={actionOpen}
				title={actionType === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
				onCancel={() => {
					setActionOpen(false)
					setActionComment('')
				}}
				onOk={onConfirmAction}
				okButtonProps={{
					loading: actionLoading,
					danger: actionType === 'reject',
				}}
				okText={actionType === 'approve' ? 'Approve' : 'Reject'}
			>
				<Input.TextArea
					rows={4}
					placeholder={
						actionType === 'approve'
							? 'Optional comment'
							: 'Reason for rejection (required)'
					}
					value={actionComment}
					onChange={(e) => setActionComment(e.target.value)}
				/>
			</Modal>

			{/* ─── Edit Entitlements Modal ──────────────────────────────── */}
			<Modal
				open={balanceEditOpen}
				title={`Edit Entitlements — ${balance?.year ?? new Date().getFullYear()}`}
				onCancel={() => setBalanceEditOpen(false)}
				onOk={onSaveEntitlements}
				okButtonProps={{ loading: balanceSaving }}
				okText='Save'
			>
				<Form form={balanceForm} layout='vertical' size='small'>
					{leaveTypes.map((type) => (
						<Form.Item key={type} name={type} label={`${type} Leave (days)`}>
							<InputNumber min={0} max={365} style={{ width: '100%' }} />
						</Form.Item>
					))}
				</Form>
			</Modal>
		</>
	)
}

export default LeaveDetail
